import { Node } from '@tiptap/core'
import Suggestion from '@tiptap/suggestion'
import { VueRenderer } from '@tiptap/vue-2'
import { createPopper } from '@popperjs/core'
import MentionSuggestion from '@/components/pages/forum/response/editor/ForumResponseEditorMentionSuggestion.vue'
import { t } from '@/utils/i18n'

class Mention {
  constructor({ mentionSuggestionProps } = {}) {
    const mention = Node.create({
      name: 'mention',
      atom: true,
      selectable: false,
      draggable: false,
      content: 'text*',
      isolating: true,
      addOptions() {
        return {
          inline: true,
          HTMLAttributes: { class: 'mention' },
          suggestion: {
            char: '@',
            allowSpaces: true,
            render: () => {
              let component, popup, popperInstance

              return {
                command: ({ editor, range, props }) => {
                  editor.chain().focus().setMention({ options: props, range }).run()
                },
                onStart: (props) => {
                  component = new VueRenderer(MentionSuggestion, {
                    parent: props.editor.options.element.__vue__ || props.editor.options.element,
                    propsData: { ...props, ...mentionSuggestionProps }
                  })
                  popup = component.element
                  document.body.appendChild(popup)

                  const virtualElement = {
                    getBoundingClientRect: () =>
                      props?.clientRect() || { top: 0, left: 0, width: 0, height: 0 }
                  }

                  popperInstance = createPopper(virtualElement, popup, {
                    placement: 'bottom-start',
                    modifiers: [
                      { name: 'offset', options: { offset: [0, 8] } },
                      { name: 'preventOverflow', options: { boundary: 'viewport' } },
                      { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } }
                    ]
                  })
                },
                onUpdate: (props) => {
                  if (component && popperInstance) {
                    component.updateProps({ ...props, ...mentionSuggestionProps })
                    popperInstance.update()
                  }
                },
                onKeyDown: (props) => {
                  if (!component) return false
                  if (props.event.key === 'ArrowUp') {
                    component.ref.up()
                    return true
                  }
                  if (props.event.key === 'ArrowDown') {
                    component.ref.down()
                    return true
                  }
                  if (props.event.key === 'Enter') {
                    component.ref.enter()
                    return true
                  }
                  return false
                },
                onExit: () => {
                  if (popperInstance) {
                    popperInstance.destroy()
                    popperInstance = null
                  }
                  if (popup) {
                    document.body.removeChild(popup)
                    popup = null
                  }
                  if (component) {
                    component.destroy()
                    component = null
                  }
                }
              }
            }
          }
        }
      },
      inline() {
        return this.options.inline
      },
      group() {
        return this.options.inline ? 'inline' : 'block'
      },
      addAttributes() {
        return {
          'prospect-id': { default: null },
          'user-id': { default: null },
          'full-name': { default: null },
          'mention-id': { default: null },
          'is-reply': { default: null },
          'is-anonymous': { default: null },
          'to-moderator': { default: null },
          'anonymous-id': {
            default: null,
            parseHTML: (element) => {
              const val = element.getAttribute('anonymous-id')
              return val != null ? String(val) : null
            },
            renderHTML: (attributes) => {
              if (!attributes['anonymous-id']) return {}
              return { 'anonymous-id': String(attributes['anonymous-id']) }
            }
          }
        }
      },
      renderText({ node }) {
        const isAnonymous = node.attrs['is-anonymous'] === 'true' || node.attrs['is-anonymous'] === true
        const toModerator = node.attrs['to-moderator'] === 'true' || node.attrs['to-moderator'] === true
        const userId = node.attrs['user-id']
        const prospectAccountId = node.attrs['prospect-id']
        const anonymousId = node.attrs['anonymous-id'] || ''

        if (isAnonymous && toModerator && !userId && !prospectAccountId) {
          return `@${t('forum.response.moderator_reply_user_name')}```
        }

        if (isAnonymous && !toModerator && !userId && !prospectAccountId) {
          return `@${t('forum.response.anonymous_reply_user_name_with_id', { id: anonymousId })}`
        }

        return `@${node.attrs['full-name']}`
      },
      parseHTML() {
        return [
          {
            tag: 'span.mention',
            getAttrs: (element) => {
              if (!element.classList.contains('mention')) return false
              return {
                'prospect-id': element.getAttribute('prospect-id'),
                'user-id': element.getAttribute('user-id'),
                'full-name': element.getAttribute('full-name'),
                'mention-id': element.getAttribute('mention-id'),
                'is-reply': element.getAttribute('is-reply'),
                'is-anonymous': element.getAttribute('is-anonymous'),
                'to-moderator': element.getAttribute('to-moderator'),
                'anonymous-id': element.getAttribute('anonymous-id')
              }
            }
          }
        ]
      },
      renderHTML({ HTMLAttributes }) {
        const isAnonymous =
          HTMLAttributes['is-anonymous'] === 'true' || HTMLAttributes['is-anonymous'] === true
        const toModerator =
          HTMLAttributes['to-moderator'] === 'true' || HTMLAttributes['to-moderator'] === true
        const prospectAccountId = HTMLAttributes['prospect-id']
        const userId = HTMLAttributes['user-id']
        const anonymousId = HTMLAttributes['anonymous-id'] || ''

        const fullName = HTMLAttributes['full-name'] || t('not-available')

        const text =
          isAnonymous && !userId && !prospectAccountId
            ? toModerator
              ? `@${t('forum.response.moderator_reply_user_name')}`
              : `@${t('forum.response.anonymous_reply_user_name_with_id', { id: anonymousId })}`
            : `@${fullName}`

        return [
          'span',
          {
            class: 'mention',
            'prospect-id': HTMLAttributes['prospect-id'],
            'user-id': HTMLAttributes['user-id'],
            'full-name': HTMLAttributes['full-name'],
            'mention-id': HTMLAttributes['mention-id'],
            'is-reply': HTMLAttributes['is-reply'],
            'is-anonymous': HTMLAttributes['is-anonymous'],
            'to-moderator': HTMLAttributes['to-moderator'],
            'anonymous-id': HTMLAttributes['anonymous-id']
          },
          text
        ]
      },
      addCommands() {
        return {
          setMention:
            ({ options, range }) =>
            ({ commands }) => {
              const attrs = {
                'prospect-id': options.prospectId,
                'user-id': options.userId,
                'full-name': options.fullName || t('not-available'),
                'is-reply': options.isReply,
                'is-anonymous': options.isAnonymous,
                'to-moderator': options.toModerator,
                'anonymous-id': options.anonymousId
              }
              if (range) {
                return commands.insertContentAt(range, {
                  type: this.name,
                  attrs
                })
              }
              return commands.insertContent({
                type: this.name,
                attrs
              })
            }
        }
      },
      addProseMirrorPlugins() {
        return [
          Suggestion({
            editor: this.editor,
            ...this.options.suggestion
          })
        ]
      }
    })
    return mention
  }
}

export { Mention, Mention as default }
